import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, Mail, RefreshCw, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

type Row = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  member_role: string | null;
  user_type: string | null;
  bids_placed: number; bids_30d: number; bids_pending: number; bids_won: number; bids_withdrawn: number;
  quotes_submitted: number; quotes_awarded: number;
  direct_loads_posted: number; direct_loads_live: number; direct_loads_awarded: number; bids_received: number;
  market_loads_posted: number; trucks_posted: number; offers_sent: number; offers_accepted: number;
  last_login_at: string | null; last_active_at: string | null; active_sessions: number;
};

interface Props {
  companyId: string;
  companyName: string;
  companyType: string;
}

const ago = (d: string | null) => (d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : "—");

function statsFor(r: Row, type: string): [string, number][] {
  const t = r.user_type || type;
  if (t === "shipper")
    return [["Loads posted", r.direct_loads_posted], ["Live now", r.direct_loads_live], ["Awarded", r.direct_loads_awarded], ["Bids received", r.bids_received]];
  if (t === "carrier")
    return [["Trucks posted", r.trucks_posted], ["Offers sent", r.offers_sent], ["Offers accepted", r.offers_accepted]];
  return [
    ["Bids placed", r.bids_placed], ["Bids (30d)", r.bids_30d], ["Pending", r.bids_pending], ["Won", r.bids_won],
    ["Quotes", r.quotes_submitted], ["Quotes won", r.quotes_awarded], ["Loads posted", r.market_loads_posted],
  ];
}

export function CompanyTeamActivityTab({ companyId, companyName, companyType }: Props) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const lastPull = useRef(0);

  const pull = useCallback(async () => {
    if (Date.now() - lastPull.current < 3000) return;
    lastPull.current = Date.now();
    setLoading(true);
    const { data, error } = await supabase.rpc("get_team_activity" as never, { _company_id: companyId } as never);
    if (!error) {
      setRows(((data as unknown) as Row[]) || []);
      setUpdatedAt(new Date());
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("companies").select("team_activity_enabled" as never).eq("id", companyId).maybeSingle();
      const on = !!(data as { team_activity_enabled?: boolean } | null)?.team_activity_enabled;
      if (cancelled) return;
      setEnabled(on);
      if (on) pull();
    })();
    return () => { cancelled = true; };
  }, [companyId, pull]);

  if (enabled === null) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  if (!enabled) {
    const subject = encodeURIComponent(`Team Activity feature request — ${companyName}`);
    const body = encodeURIComponent(`Hi LaneRunner team,\n\nPlease enable Team Activity for ${companyName} (company ID ${companyId}).\n\nThanks!`);
    return (
      <Card className="p-8 text-center space-y-4">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Lock className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Team Activity isn't turned on yet</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          See every user on your company account in one place — bids placed, loads won, quotes, loads posted, and when they were last active. Request access and our team will turn it on for your company.
        </p>
        <Button asChild>
          <a href={`mailto:support@lanerunner.com?subject=${subject}&body=${body}`}>
            <Mail className="h-4 w-4 mr-2" /> Request this feature
          </a>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">Team Activity</h3>
          <span className="text-xs text-muted-foreground">
            {updatedAt ? `Updated ${formatDistanceToNow(updatedAt, { addSuffix: true })}` : ""}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={pull} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Pull latest activity
        </Button>
      </div>

      {rows.length === 0 && !loading && (
        <Card className="p-6 text-sm text-muted-foreground text-center">No active users on this company yet.</Card>
      )}

      {rows.map((r) => (
        <Card key={r.user_id} className="p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2 min-w-0">
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">{r.full_name || r.email || "Unnamed user"}</div>
              <div className="text-xs text-muted-foreground truncate">{r.email}</div>
            </div>
            <div className="flex items-center gap-2">
              {r.member_role === "company_admin" && <Badge variant="secondary">Owner / Admin</Badge>}
              {r.user_type && <Badge variant="outline" className="capitalize">{r.user_type}</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {statsFor(r, companyType).map(([label, v]) => (
              <div key={label} className="rounded-md bg-muted/50 px-3 py-2">
                <div className="text-lg font-bold text-foreground">{Number(v) || 0}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Last active: {ago(r.last_active_at)}</span>
            <span>Last login: {ago(r.last_login_at)}</span>
            <span>Devices: {Number(r.active_sessions) || 0}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}
